export type ActivationWizardFallbackMode = 'none' | 'simplified' | 'generic'

export interface ActivationWizardPublishCorrelation {
  wizardSessionId: string
  wizardId: string | null
  wizardName: string | null
  wizardKind: string | null
  creationMode: string | null
  teamPid: string | number | null
  repairApplied: boolean
  fallbackMode: ActivationWizardFallbackMode
  completedAt: string
}

interface PendingActivationWizardPublishCorrelation
  extends ActivationWizardPublishCorrelation {
  publishClickedAt: string
}

const COMPLETION_STORAGE_KEY = 'activation_wizard_publish_correlation'
const PENDING_PUBLISH_STORAGE_KEY = 'activation_wizard_publish_pending_correlation'
const COMPLETION_MAX_AGE_MS = 1000 * 60 * 30
const PENDING_PUBLISH_MAX_AGE_MS = 1000 * 60 * 10

const hasBrowserSessionStorage = () =>
  typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined'

const readStoredCorrelation = <T>(storageKey: string, maxAgeMs: number): T | null => {
  if (!hasBrowserSessionStorage()) return null

  const raw = window.sessionStorage.getItem(storageKey)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as T & {
      completedAt?: string
      publishClickedAt?: string
    }
    const timestamp = parsed.publishClickedAt ?? parsed.completedAt ?? null
    const recordedAtMs = timestamp ? Date.parse(timestamp) : Number.NaN

    if (!Number.isFinite(recordedAtMs) || Date.now() - recordedAtMs > maxAgeMs) {
      window.sessionStorage.removeItem(storageKey)
      return null
    }

    return parsed
  } catch {
    window.sessionStorage.removeItem(storageKey)
    return null
  }
}

const clearStoredCorrelation = (storageKey: string) => {
  if (!hasBrowserSessionStorage()) return
  window.sessionStorage.removeItem(storageKey)
}

export const saveActivationWizardPublishCorrelation = (
  payload: ActivationWizardPublishCorrelation
) => {
  if (!hasBrowserSessionStorage()) return
  clearStoredCorrelation(PENDING_PUBLISH_STORAGE_KEY)
  window.sessionStorage.setItem(COMPLETION_STORAGE_KEY, JSON.stringify(payload))
}

export const getActivationWizardPublishCorrelation = ():
  | ActivationWizardPublishCorrelation
  | null => {
  return readStoredCorrelation<ActivationWizardPublishCorrelation>(
    COMPLETION_STORAGE_KEY,
    COMPLETION_MAX_AGE_MS
  )
}

export const consumeActivationWizardPublishCorrelationForPublishClick = ():
  | ActivationWizardPublishCorrelation
  | null => {
  const correlation = getActivationWizardPublishCorrelation()
  if (!correlation || !hasBrowserSessionStorage()) return correlation

  const pendingCorrelation: PendingActivationWizardPublishCorrelation = {
    ...correlation,
    publishClickedAt: new Date().toISOString()
  }

  window.sessionStorage.setItem(
    PENDING_PUBLISH_STORAGE_KEY,
    JSON.stringify(pendingCorrelation)
  )
  clearStoredCorrelation(COMPLETION_STORAGE_KEY)

  return correlation
}

export const getPendingActivationWizardPublishCorrelation = ():
  | ActivationWizardPublishCorrelation
  | null => {
  const pendingCorrelation = readStoredCorrelation<PendingActivationWizardPublishCorrelation>(
    PENDING_PUBLISH_STORAGE_KEY,
    PENDING_PUBLISH_MAX_AGE_MS
  )

  if (!pendingCorrelation) return null

  const { publishClickedAt: _publishClickedAt, ...correlation } = pendingCorrelation
  return correlation
}

export const clearActivationWizardPublishCorrelation = () => {
  clearStoredCorrelation(COMPLETION_STORAGE_KEY)
  clearStoredCorrelation(PENDING_PUBLISH_STORAGE_KEY)
}
